'use client';

import { AlertTriangle, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_VALUE_TEXT
} from '@/components/attributes';
import { cn } from '@/lib/utils';
import type { InstitutionDuplicateCandidate } from '@/lib/services/institutions/types';
import { formatInstitutionAddedDate } from './institution-descriptor';

/**
 * **"You already have this one" as a third step inside the search popover**
 * (VALE-1422).
 *
 * A firm created the same aged-care facility on consecutive days, on different
 * matters — `Respect Aged Care - Devonport TAS` then
 * `Respect Aged Care Devonport TAS`, one hyphen apart, same email address. Every
 * duplicate check on the create compared names with punctuation intact, so both
 * reads were "a new organisation", and the second row was created in silence. The
 * firm is now stuck with two records for one place, and a firm-scoped row carries
 * none of the catalogue row's verified contact details or forms.
 *
 * ## It is a question, not a refusal
 *
 * Two facilities genuinely can share a name — an operator runs dozens of homes, a
 * council appears legitimately under several names — so **`Add anyway` is a real
 * action and always available**. A guard that could not be overridden would be worse
 * than the duplicate it prevents: it would make a legitimate record impossible to
 * create at all.
 *
 * `Use this one` is the primary action because it is almost always the right answer,
 * and because taking a *catalogue* row is strictly better than minting a copy of it.
 *
 * ## Why each candidate carries the lines it does
 *
 * The reader has to answer "is this the place I meant?" without remembering what they
 * did last week — that is the whole failure. So each candidate shows where it came
 * from and whether it is in use: **its own institution type** (VALE-1585 — the matcher
 * is deliberately type-blind, so a candidate can be filed under a different type from
 * the field the reader is creating from, and nothing else on the panel says so),
 * **provenance and a date** for a row the firm added, **the forms Vale holds** for a
 * catalogue row, the contact email for both, and whether any matter uses it. There is
 * no suburb or postcode on an institution (the location lives inside the name), so
 * these lines cannot invent geography.
 *
 * **One fact per line, not one joined string.** Joined with separators it wrapped
 * mid-list at 272px, stranding a `·` at a line end and a single word on the next.
 *
 * No badge on the row. Provenance belongs in the text, where it can say something
 * specific, rather than in a chip that repeats a word on every row.
 *
 * ## Chrome
 *
 * Built from the attribute treatment's tokens, and it shares
 * {@link InlineCreateStep}'s step header, so the popover reads as having gone
 * somewhere it can come back from. `Back` returns to the create form with the typed
 * values intact — the reader may want to correct the name rather than choose either
 * option, and losing what they typed would be its own defect.
 */
export function NearDuplicateStep({
  title,
  typedName,
  candidates,
  onUseExisting,
  onAddAnyway,
  onBack,
  busy = false,
  headline,
  footerPrompt,
  footerActionLabel,
  footerActionBusyLabel
}: {
  /** Names the step — `Already added`. */
  title: string;
  /** What the reader typed, so they can see what it was matched against. */
  typedName: string;
  candidates: InstitutionDuplicateCandidate[];
  onUseExisting: (candidate: InstitutionDuplicateCandidate) => void;
  onAddAnyway: () => void;
  /**
   * Back to the create form. Omitted where there is nowhere to go back TO — the
   * archived panel (VALE-1497) is raised by the server after the create was already
   * submitted, so it is shown in a dialog of its own rather than as a step inside the
   * popover, and a `Back` chevron there would point at nothing.
   */
  onBack?: () => void;
  /** True while the create that follows `Add anyway` is in flight. */
  busy?: boolean;
  /**
   * Replaces the "Your firm can already see …" warning (VALE-1497).
   *
   * The default states what the panel found. The archived case has found something
   * different — a row this firm RETIRED — and saying "you already have one" of a row
   * the reader deliberately removed is the wrong sentence, so the caller supplies its
   * own. Everything below it is unchanged, because the question being asked is the
   * same one: which of these did you mean?
   */
  headline?: string;
  /** Replaces the footer's "A different one?" prompt. */
  footerPrompt?: string;
  /** Replaces the footer's `Add anyway` label. */
  footerActionLabel?: string;
  /** Replaces the footer action's in-flight label. Supplied with {@link footerActionLabel}. */
  footerActionBusyLabel?: string;
}) {
  const single = candidates.length === 1;

  return (
    <div className="flex flex-col">
      <div className="border-border-subtle flex items-center gap-1 border-b px-2 py-1.5">
        {onBack && (
          <Button
            tone="muted"
            pad="xs"
            weight="normal"
            type="button"
            variant="ghost"
            size="2xs"
            onClick={onBack}
          >
            <ChevronLeft className="h-3.5 w-3.5 shrink-0" />
            Back
          </Button>
        )}
        <span
          className={cn(
            ATTRIBUTE_LABEL_TEXT,
            'text-foreground-secondary font-medium'
          )}
        >
          {title}
        </span>
      </div>

      <div className="flex flex-col gap-3 px-3 pt-3 pb-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="text-warning-subtle-foreground mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p
            className={cn(
              ATTRIBUTE_LABEL_TEXT,
              'text-warning-subtle-foreground'
            )}
          >
            {/*
              Never asserts a type here (VALE-1585) — the duplicate check is
              deliberately type-blind, so a candidate can be a different type from
              the field the reader is creating from. Each candidate's own line
              carries its type; this can only say a name matched.
            */}
            {headline ??
              (single
                ? 'Your firm can already see a match for this name.'
                : `Your firm can already see ${candidates.length} matches for this name.`)}
          </p>
        </div>

        {/*
          What the reader typed, quoted back. Without it the panel asserts a match
          against something off-screen — the search box is replaced by this step.
        */}
        <p className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
          You typed “{typedName}”.
        </p>

        {/*
          Bounded and internally scrollable (VALE-1613) — unbounded, this list grew
          to the match count and pushed the popover's own footer controls off the
          bottom of the window. The `dvh` clamp mirrors `CommandList`'s (see
          `InstitutionSearchField`'s search-results list below) so it also holds on
          a short laptop window, not just a fixed pixel count.
        */}
        <div className="flex max-h-[min(320px,45dvh)] flex-col gap-3.5 overflow-y-auto overscroll-contain">
          {candidates.map((candidate) => (
            <div
              key={candidate.institutionId}
              className="border-border bg-card flex flex-col gap-1.5 rounded-md border p-2.5"
            >
              <div className="flex flex-col gap-0.5">
                <span
                  className={cn(
                    ATTRIBUTE_VALUE_TEXT,
                    'text-foreground font-medium'
                  )}
                >
                  {candidate.name}
                </span>
                {/*
                  Stacked short lines, not one separator-joined string. Joined, it
                  wrapped mid-list at 272px and left a dangling `·` at a line end
                  with a single orphan word ("matter") on the next — which reads as
                  unfinished rather than dense. Each line is now one fact, short
                  enough not to wrap, and the email gets its own line because it is
                  the one unbreakable token.
                */}
                {describeCandidate(candidate).map((line) => (
                  <span
                    key={line}
                    className={cn(
                      ATTRIBUTE_LABEL_TEXT,
                      ATTRIBUTE_MUTED_INK,
                      'truncate'
                    )}
                  >
                    {line}
                  </span>
                ))}
              </div>
              <div className="flex">
                <Button
                  size="xs"
                  density="compact"
                  type="button"
                  disabled={busy}
                  onClick={() => onUseExisting(candidate)}
                >
                  Use this one
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/*
          Secondary, and deliberately not styled as a warning. Choosing it is a
          legitimate answer, not a mistake being tolerated — two facilities really can
          share a name, and the decision is recorded rather than merely permitted.
        */}
        {/*
          `-mx-3` so this rule runs full-bleed like the header's. Inset, the two
          hairlines were the same weight and colour at different widths, which reads
          as an unintentional difference rather than a distinction.
        */}
        <div className="border-border-subtle -mx-3 flex items-center justify-between gap-2 border-t px-3 pt-3">
          {/*
            Short enough to sit on one line beside the button at 272px. "Genuinely a
            different place?" wrapped and left "place?" alone with 40px of clear space
            next to it.
          */}
          {/*
            The type word, not "place". Calling the same object a "facility" in the
            warning and a "place" in the footer is two vocabularies for one thing
            inside a 330px panel.
          */}
          <span className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
            {/* Same reasoning as the headline above: no type assertion (VALE-1585). */}
            {footerPrompt ?? 'A different one?'}
          </span>
          <Button
            size="xs"
            density="compact"
            type="button"
            variant="outline"
            disabled={busy}
            onClick={onAddAnyway}
          >
            {busy
              ? (footerActionBusyLabel ?? 'Adding…')
              : (footerActionLabel ?? 'Add anyway')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The lines that make a candidate identifiable, one fact each, ordered by what
 * actually distinguishes it: what kind of thing it is, where it came from, how to
 * recognise it, whether it is in use.
 *
 * **The type leads** (VALE-1585). The near-duplicate check is deliberately
 * type-blind, so a candidate can be filed under a different institution type from
 * the field the reader opened this panel from — the headline and footer no longer
 * say what type the candidates are, so this is the only place that does, and it is
 * the fact most worth reading first when the panel is offering a cross-type match.
 *
 * Returns lines rather than a joined string deliberately — see the call site.
 */
function describeCandidate(candidate: InstitutionDuplicateCandidate): string[] {
  const lines: string[] = [candidate.type];

  if (candidate.isGlobal) {
    lines.push(
      candidate.formCount > 0
        ? `Vale catalogue · ${candidate.formCount} form${candidate.formCount === 1 ? '' : 's'}`
        : 'Vale catalogue'
    );
  } else {
    lines.push(
      // Not "Added by X · added 1 Sept" — that said "added" twice in one short line.
      `${
        candidate.lawFirmName
          ? `Added by ${candidate.lawFirmName}`
          : 'Added by your firm'
      }, ${formatInstitutionAddedDate(candidate.createdAt)}`
    );
  }

  if (candidate.email) {
    lines.push(candidate.email);
  }

  lines.push(
    candidate.matterCount > 0
      ? `Used on ${candidate.matterCount} matter${candidate.matterCount === 1 ? '' : 's'}`
      : 'Not used yet'
  );

  return lines;
}
